import React from 'react';
import PersonIcon from '@mui/icons-material/Person';
import { useDispatch } from 'react-redux';
import useTailwindBrand from 'src/Theme/useTailwindBrand';
import FormInput from 'src/Components/Ui/FormInput';
import FormSelect from 'src/Components/FormFields/FormSelect';
import FormRadioYesNo from 'src/Components/FormFields/FormRadioYesNo';
import FormSection from 'src/Components/FormFields/FormSection';
import { useProcuration } from 'src/Hooks/useProcuration';
import { useProcurationContext } from 'src/Contexts/ProcurationContext';
import { FormErrorsSecondPart } from 'src/Hooks/InterfaceProcuration';
import LayoutProcuration from 'src/Components/Procuration/LayoutProcuration';

const SecondPartAe = () => {
    const { classes } = useTailwindBrand();
    const dispatch = useDispatch();
    const context = useProcurationContext();
    const { teachr, prevStep, nextStep, pdfDoc, triggerPdfUpdate, generalProcuration, setPage } = context;

    const {
        formDataSecondPart,
        errorsSecondPart,
        pdfFiledFieldsSecond,
        setFormDataSecondPart,
        setErrorsSecondPart,
        setHealthInsuranceField,
        setSocialTaxField,
        setIsAeField,
        setIsActivityField,
        handleChange,
        handleSelect,
    } = useProcuration({ pdfDoc, teachr, triggerPdfUpdate, setPage });

    const values = {
        setField: setFormDataSecondPart,
        error: errorsSecondPart,
        setError: setErrorsSecondPart
    }

    const validate = (): boolean => {
        const newErrors: FormErrorsSecondPart = {
            hasActivity: formDataSecondPart.hasActivity === 0 ? 'Veuillez répondre à cette question' : '',
            isAe: formDataSecondPart.isAe === 0 ? 'Veuillez répondre à cette question' : '',
            socialTaxMode: formDataSecondPart.socialTaxMode === 0 ? 'Veuillez sélectionner un mode de versement' : '',
            // paymentMode: formDataSecondPart.paymentMode === 0 ? 'Veuillez sélectionner un mode de versement' : '',
            socialSecurityNumber: formDataSecondPart.socialSecurityNumber.trim() === '' ? 'Veuillez renseigner votre numéro de sécurité sociale' : '',
            healthInsurance: formDataSecondPart.healthInsurance === 0 ? 'Veuillez sélectionner un organisme' : '',
            healthInsuranceOther: formDataSecondPart.healthInsurance === 6 && formDataSecondPart.healthInsuranceOther.trim() === '' ? 'Veuillez préciser votre organisme' : '',
            activityName: formDataSecondPart.hasActivity === 1 && formDataSecondPart.activityName.trim() === '' ? 'Veuillez renseigner le nom de votre activité' : ''
        };
        setErrorsSecondPart(newErrors);
        return !Object.values(newErrors).some(error => error !== '');
    };


    const handleNext = () => {
        if (validate()) {
            const payload = {
                step: 4,
                secondProc: generalProcuration?.secondProc || {},
                thirdProc: generalProcuration?.thirdProc || {},
                fourthProc: formDataSecondPart,
                fifthProc: generalProcuration?.fifthProc || {},
                sixthProc: generalProcuration?.sixthProc || {},
                legalRepProc: generalProcuration?.legalRepProc || [],
                seventhProc: generalProcuration?.seventhProc || {},
                eightProc: generalProcuration?.eightProc || {}
            };
            dispatch({ type: 'SET_AE_INFO', payload });
            nextStep(4);
        }
    };



    return (
        <LayoutProcuration
            title="Informations complémentaires"
            icon={<PersonIcon className={`w-6 h-6 ${classes.primaryText}`} />}
            nextStep={handleNext}
            prevStep={prevStep}
            currentStep={1}
            totalSteps={4}
        >
            <FormSection title="Sécurité sociale">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <FormInput
                        label="Numéro de sécurité sociale"
                        type="text"
                        placeholder="Ex: 1 85 08 75 115 036 45"
                        value={formDataSecondPart.socialSecurityNumber}
                        error={errorsSecondPart.socialSecurityNumber}
                        onChange={(e) => handleChange({ field: 'socialSecurityNumber', pdfField: pdfFiledFieldsSecond.socialSecurityNumberField }, e, values, 3)}
                        name="socialSecurityNumber"
                        required
                    />

                    <FormSelect
                        callback={(value) => handleSelect(value, 'healthInsurance', setHealthInsuranceField, values, 3)}
                        required={true}
                        error={errorsSecondPart.healthInsurance}
                        label="Organisme d'assurance maladie"
                        placeholder="Sélectionner un organisme"
                        data={['Agricole', 'Non salarié', 'ENIM', 'Non agricole', 'Régime Général', 'Autre']}
                        customClass={`bg-white ${classes.fontFamily} py-[6px] shadow-md shadow-[rgba(0,0,0,25%)] text-[16px] border ${errorsSecondPart.healthInsurance ? `${classes.errorBorder} border-l-4` : 'hover:border-2 hover:border-gray-400'}`}
                        defaultValue={formDataSecondPart.healthInsurance}
                    />

                    {formDataSecondPart.healthInsurance === 6 && (
                        <div className="lg:col-span-2">
                            <FormInput
                                label="Si autre, précisez votre organisme d'assurance maladie"
                                type="text"
                                placeholder="Ex: Nom de l'organisme"
                                value={formDataSecondPart.healthInsuranceOther}
                                error={errorsSecondPart.healthInsuranceOther}
                                onChange={(e) => handleChange({ field: 'healthInsuranceOther', pdfField: pdfFiledFieldsSecond.healthInsuranceOtherField }, e, values, 3)}
                                name="healthInsuranceOther"
                                required
                            />
                        </div>
                    )}
                </div>
            </FormSection>
            <FormSection title="Cotisations sociales">
                <FormSelect
                    callback={(value) => handleSelect(value, 'socialTaxMode', setSocialTaxField, values, 3)}
                    required={true}
                    error={errorsSecondPart.socialTaxMode}
                    label="Mode de versement de vos cotisations sociales"
                    placeholder="Sélectionner un mode"
                    data={['Mensuel', 'Trimestriel']}
                    customClass={`bg-white ${classes.fontFamily} py-[6px] shadow-md shadow-[rgba(0,0,0,25%)] text-[16px] border ${errorsSecondPart.socialTaxMode ? `${classes.errorBorder} border-l-4` : 'hover:border-2 hover:border-gray-400'}`}
                    defaultValue={formDataSecondPart.socialTaxMode}
                />
            </FormSection>
            <FormSection title="Activité professionnelle">
                <div className='space-y-2'>
                    <FormRadioYesNo
                        label="Avez-vous exercé une activité non salariée en France ?"
                        value={formDataSecondPart.hasActivity}
                        onChange={(value) => handleSelect(value, 'hasActivity', setIsActivityField, values, 3)}
                        error={errorsSecondPart.hasActivity}
                        required={true}
                        name="hasActivity"
                    />
                    {formDataSecondPart.hasActivity === 1 && (
                        <div className="lg:col-span-2">
                            <FormInput
                                label="Quel était votre numéro de SIRET ?"
                                type="text"
                                placeholder="Ex: Numéro de SIRET"
                                value={formDataSecondPart.activityName}
                                error={errorsSecondPart.activityName}
                                onChange={(e) => handleChange({ field: 'activityName', pdfField: pdfFiledFieldsSecond.activityNameField }, e, values, 3)}
                                name="activityName"
                                required
                            />
                        </div>
                    )}
                    <FormRadioYesNo
                        label="Avez-vous déjà eu le statut auto-entrepreneur par le passé ?"
                        value={formDataSecondPart.isAe}
                        onChange={(value) => handleSelect(value, 'isAe', setIsAeField, values, 3)}
                        error={errorsSecondPart.isAe}
                        required={true}
                        name="isAe"
                    />
                </div>
            </FormSection>


        </LayoutProcuration>
    );
};

export default SecondPartAe;
